import { useCallback, useEffect, useState } from 'react';

import { calibrationConstants } from '../../helpers/calibration-constants';
import { useCalibration } from '../../context/calibrationContext';
import { Flex, RemoteVideo, theme } from '../../2022-component-library';

const BlindSpots = ({ blindSpotData, setBlindSpotData, blindSpotUpdateMessage, setBlindSpotUpdateMessage, postMessage }) => {
  const [remoteParticipantMarginLeft, setRemoteParticipantMarginLeft] = useState(0);
  const { blindSpotThumbnailWidthPx, blindSpotThumbnailHeightPx, tangent15degrees, clientYmmArchDegree } = calibrationConstants;
  const { updateCalibrationData } = useCalibration();

  const updateBlindspotDistance = useCallback(
    (blindSpotXPosition, blindSpotVideoXPosition, dataFromOptician) => {
      const { blindSpotThumbnailHeightPx, blindSpotThumbnailWidthPx, blindSpotThumbnailY, circleDimensionsPx, pixelDensity } = dataFromOptician;

      let blindSpotVanishingXDistancePx = 0;
      let calcBlindSpotVideoXPosition = blindSpotVideoXPosition + blindSpotThumbnailWidthPx / 2;
      let calcBlindSpotXPosition = blindSpotXPosition + circleDimensionsPx / 2;

      if (blindSpotVideoXPosition && blindSpotVideoXPosition > blindSpotXPosition) {
        blindSpotVanishingXDistancePx = calcBlindSpotVideoXPosition - calcBlindSpotXPosition;
      } else {
        blindSpotVanishingXDistancePx = calcBlindSpotXPosition - calcBlindSpotVideoXPosition;
      }

      if (pixelDensity) {
        const blindSpotVanishingXDistanceMm = blindSpotVanishingXDistancePx * pixelDensity;
        const distanceFromScreen = blindSpotVanishingXDistanceMm / tangent15degrees;
        const clientYmm = blindSpotVanishingXDistanceMm * clientYmmArchDegree;
        const clientYpx = clientYmm / pixelDensity;

        const dataFromPatient = {
          blindSpotVanishingXDistancePx: blindSpotVanishingXDistancePx,
          distanceFromScreen: distanceFromScreen,
        };

        const blindSpotUpdateMessage = {
          type: 'blindSpotUpdate',
          content: { ...dataFromOptician, ...dataFromPatient },
        };

        postMessage(blindSpotUpdateMessage);

        updateCalibrationData({ blindSpotVanishingXDistancePx: blindSpotVanishingXDistancePx });

        setBlindSpotData((blindSpotData) => ({
          ...blindSpotData,
          blindSpot: {
            clientX: blindSpotXPosition,
            clientY: blindSpotThumbnailY + blindSpotThumbnailHeightPx / 2 + clientYpx,
            circleDimensionsPx: circleDimensionsPx,
            show: true,
          },
        }));
      }
    },
    [clientYmmArchDegree, postMessage, setBlindSpotData, tangent15degrees, updateCalibrationData],
  );

  useEffect(() => {
    if (blindSpotUpdateMessage) {
      const { type, msgBody } = blindSpotUpdateMessage;

      if (type === 'blindSpotXPosition') {
        updateBlindspotDistance(msgBody.clientX, blindSpotData.blindSpotVideo.clientX, msgBody.dataFromOptician);
      } else if (type === 'blindSpotVideoXPosition') {
        updateBlindspotDistance(blindSpotData.blindSpot.clientX, msgBody.clientX, msgBody.dataFromOptician);
        setBlindSpotData((blindSpotData) => ({ ...blindSpotData, blindSpotVideo: msgBody }));
      }

      setBlindSpotUpdateMessage(null);
    }
  }, [blindSpotUpdateMessage, setBlindSpotUpdateMessage, setBlindSpotData, blindSpotData, updateBlindspotDistance]);

  useEffect(() => {
    setRemoteParticipantMarginLeft(blindSpotData.blindSpotVideo.clientX);
  }, [blindSpotData.blindSpotVideo.clientX]);

  return (
    <Flex backgroundColor='#fff' width='100vw' height='100vh'>
      <Flex
        position='absolute'
        backgroundColor={blindSpotData.blindSpot.show ? 'black' : 'white'}
        height={`${blindSpotData.blindSpot.circleDimensionsPx}px`}
        width={`${blindSpotData.blindSpot.circleDimensionsPx}px`}
        marginLeft={`${blindSpotData.blindSpot.clientX}px`}
        marginTop={`${blindSpotData.blindSpot.clientY}px`}
        borderRadius='50%'
      />
      <Flex
        id='video-container'
        position='fixed'
        width={`${blindSpotThumbnailWidthPx}px`}
        height={`${blindSpotThumbnailHeightPx}px`}
        marginLeft={`${remoteParticipantMarginLeft}px`}
        //marginTop is 20px + 30px + 160px, to account for styling in legacy VideoChat component
        marginTop='210px'
        borderRadius={`${calibrationConstants.VIDEO_BORDER_RADIUS}px`}
        boxShadow={theme.elevations.elevation2}
      >
        <RemoteVideo />
      </Flex>
    </Flex>
  );
};

export default BlindSpots;
